import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  PageHeader,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Input,
  Select,
  Checkbox,
  Spinner,
  Badge,
  Alert,
  Modal,
  ModalFooter,
} from '@orientamos/ui'
import {
  FileCheck,
  FileText,
  Save,
  CheckCircle,
  Download,
  Printer,
  Eye,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Maximize2,
  X,
} from 'lucide-react'
import {
  listIntakes,
  getIntake,
  listTemplates,
  getTemplate,
  listClauses,
  getContract,
  createContract,
  updateContract,
  renderContract,
  approveContract,
  exportContractHtml,
  exportContractPdfBlob,
  type IntakeListItem,
  type IntakeDetail,
  type TemplateListItem,
  type TemplateDetail,
  type ClauseListItem,
  type ContractDetail,
} from '@lib/api'
import { printContract } from '@lib/printContract'
import { buildContractSrcDoc } from '@lib/contractPreview'
import { buildWordHtml } from '@lib/buildWordHtml'
import { formatCurrency, formatDate } from '@orientamos/shared'

const fieldLabelTranslations: Record<string, string> = {
  owner: 'Propietario',
  tenant: 'Arrendatario',
  guarantor: 'Deudor Solidario',
  property: 'Inmueble',
  terms: 'Terminos',
  firstName: 'Nombre',
  lastName: 'Apellido',
  idNumber: 'Cedula',
  idIssuedAt: 'Lugar de expedicion',
  email: 'Correo',
  phone: 'Telefono',
  address: 'Direccion',
  city: 'Ciudad',
  profession: 'Profesion',
  personType: 'Tipo de persona',
  companyName: 'Razon social',
  nit: 'NIT',
  legalRepName: 'Representante legal',
  legalRepId: 'Cedula representante',
  propertyType: 'Tipo de inmueble',
  stratum: 'Estrato',
  area: 'Area',
  buildingName: 'Edificio/Conjunto',
  parking: 'Parqueadero',
  storage: 'Deposito',
  monthlyRent: 'Canon mensual',
  administrationFee: 'Administracion',
  startDate: 'Fecha inicio',
  endDate: 'Fecha fin',
  durationMonths: 'Duracion (meses)',
  rentIncreaseFormula: 'Formula de incremento',
  gracePeriodDays: 'Dias de gracia',
  additionalClauses: 'Clausulas adicionales',
}

function translateFieldLabel(key: string): string {
  const parts = key.split('.')
  return parts
    .map(
      (p) =>
        fieldLabelTranslations[p] ||
        p
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim()
    )
    .join(' - ')
}

interface FieldOverride {
  key: string
  label: string
  value: string
  originalValue: string
}

export default function ContractGeneratorPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isEditMode = Boolean(id)
  const preselectedIntakeId = searchParams.get('intakeId')

  // Loading states
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const [isExportingDocx, setIsExportingDocx] = useState(false)
  const [isRendering, setIsRendering] = useState(false)

  // Data
  const [contract, setContract] = useState<ContractDetail | null>(null)
  const [intakes, setIntakes] = useState<IntakeListItem[]>([])
  const [templates, setTemplates] = useState<TemplateListItem[]>([])
  const [clauses, setClauses] = useState<ClauseListItem[]>([])
  const [selectedIntake, setSelectedIntake] = useState<IntakeDetail | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateDetail | null>(null)

  // Form state
  const [intakeId, setIntakeId] = useState(preselectedIntakeId || '')
  const [templateId, setTemplateId] = useState('')
  const [selectedClauseIds, setSelectedClauseIds] = useState<string[]>([])
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, unknown>>({})
  const [renderedHtml, setRenderedHtml] = useState<string | null>(null)

  // UI state
  const [showOverrides, setShowOverrides] = useState(false)
  const [approveModalOpen, setApproveModalOpen] = useState(false)
  const [fullscreenOpen, setFullscreenOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Load intakes and templates in parallel
        const [intakesRes, templatesRes, clausesRes] = await Promise.all([
          listIntakes({ limit: 100 }),
          listTemplates({ isActive: true, limit: 100 }),
          listClauses({ isActive: true, limit: 100 }),
        ])

        if (intakesRes.success) {
          setIntakes(intakesRes.data)
        }
        if (templatesRes.success) {
          setTemplates(templatesRes.data)
        }
        if (clausesRes.success) {
          setClauses(clausesRes.data)
        }

        // If editing, load the contract
        if (isEditMode && id) {
          const contractRes = await getContract(id)
          if (contractRes.success && contractRes.data) {
            const c = contractRes.data
            setContract(c)
            setIntakeId(c.intakeId)
            setTemplateId(c.templateId)
            setSelectedClauseIds(c.selectedClauses || [])
            setFieldOverrides(c.fieldOverrides || {})
            setRenderedHtml(c.renderedHtml)

            // Load intake and template details
            const [intakeRes, templateRes] = await Promise.all([
              getIntake(c.intakeId),
              getTemplate(c.templateId),
            ])
            if (intakeRes.success && intakeRes.data) {
              setSelectedIntake(intakeRes.data)
            }
            if (templateRes.success && templateRes.data) {
              setSelectedTemplate(templateRes.data)
            }
          } else {
            setError('Contrato no encontrado')
          }
        } else if (preselectedIntakeId) {
          // If creating with preselected intake
          const intakeRes = await getIntake(preselectedIntakeId)
          if (intakeRes.success && intakeRes.data) {
            setSelectedIntake(intakeRes.data)
          }
        }
      } catch (err) {
        console.error('Error loading data:', err)
        setError('Error al cargar los datos')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [id, isEditMode, preselectedIntakeId])

  // Load intake details when selected
  useEffect(() => {
    if (intakeId && !isEditMode) {
      getIntake(intakeId).then((res) => {
        if (res.success && res.data) {
          setSelectedIntake(res.data)
        }
      })
    }
  }, [intakeId, isEditMode])

  // Load template details when selected
  useEffect(() => {
    if (templateId && !isEditMode) {
      getTemplate(templateId).then((res) => {
        if (res.success && res.data) {
          setSelectedTemplate(res.data)
        }
      })
    }
  }, [templateId, isEditMode])

  // Build field overrides list from template fields
  const fieldOverridesList = useMemo((): FieldOverride[] => {
    if (!selectedTemplate?.fields) return []

    return selectedTemplate.fields.map((field) => {
      const overrideValue = fieldOverrides[field.key]
      let originalValue = ''

      // Try to get the original value from intake data
      if (selectedIntake && field.source) {
        const source = field.source.toLowerCase()
        if (source === 'property' && field.sourcePath) {
          originalValue = String(
            (selectedIntake.property as Record<string, unknown>)?.[field.sourcePath] || ''
          )
        } else if (source === 'terms' && field.sourcePath) {
          const val = (selectedIntake.terms as Record<string, unknown>)?.[field.sourcePath]
          if (typeof val === 'number') {
            originalValue =
              field.sourcePath.includes('Rent') || field.sourcePath.includes('Fee')
                ? formatCurrency(val)
                : String(val)
          } else {
            originalValue = String(val || '')
          }
        } else if (source === 'tenant' && field.sourcePath) {
          const tenant = selectedIntake.parties.find((p) => p.role === 'tenant')
          if (tenant) {
            originalValue = String(
              (tenant as unknown as Record<string, unknown>)?.[field.sourcePath] || ''
            )
          }
        }
      }

      return {
        key: field.key,
        label: translateFieldLabel(field.key),
        value: overrideValue !== undefined ? String(overrideValue) : '',
        originalValue,
      }
    })
  }, [selectedTemplate, selectedIntake, fieldOverrides])

  // Filter clauses by contract type
  const filteredClauses = useMemo(() => {
    if (!selectedIntake?.contractType) return clauses
    return clauses.filter(
      (c) => c.contractTypes.length === 0 || c.contractTypes.includes(selectedIntake.contractType)
    )
  }, [clauses, selectedIntake?.contractType])

  // Group clauses by category
  const groupedClauses = useMemo(() => {
    const groups: Record<string, ClauseListItem[]> = {}
    for (const clause of filteredClauses) {
      const category = clause.category
      if (!groups[category]) {
        groups[category] = []
      }
      groups[category]!.push(clause)
    }
    return groups
  }, [filteredClauses])

  const categoryLabels: Record<string, string> = {
    services: 'Servicios',
    grace_period: 'Periodo de gracia',
    rent_increase: 'Incremento de renta',
    renewal: 'Renovacion',
    pets: 'Mascotas',
    modifications: 'Modificaciones',
    insurance: 'Seguros',
    utilities: 'Servicios publicos',
    other: 'Otros',
  }

  const handleFieldOverrideChange = (key: string, value: string) => {
    setFieldOverrides((prev) => ({
      ...prev,
      [key]: value || undefined, // Remove empty values
    }))
  }

  const handleClauseToggle = (clauseId: string) => {
    setSelectedClauseIds((prev) =>
      prev.includes(clauseId) ? prev.filter((id) => id !== clauseId) : [...prev, clauseId]
    )
  }

  const handleSave = async () => {
    if (!intakeId || !templateId) {
      setError('Selecciona una orden de contrato y una plantilla')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      if (isEditMode && id) {
        // Update existing contract
        const result = await updateContract(id, {
          selectedClauses: selectedClauseIds,
          fieldOverrides,
        })

        if (result.success) {
          // Re-fetch the rendered HTML
          const renderRes = await renderContract(id)
          if (renderRes.success && renderRes.data) {
            setRenderedHtml(renderRes.data.renderedHtml)
          }
        } else {
          setError(result.error || 'Error al actualizar el contrato')
        }
      } else {
        // Create new contract
        const result = await createContract({
          intakeId,
          templateId,
          selectedClauses: selectedClauseIds,
          fieldOverrides,
        })

        if (result.success && result.data) {
          navigate(`/contracts/${result.data.id}/edit`)
        } else {
          setError(result.error || 'Error al crear el contrato')
        }
      }
    } catch (err) {
      console.error('Error saving:', err)
      setError('Error al guardar el contrato')
    } finally {
      setIsSaving(false)
    }
  }

  const handleRender = async () => {
    if (!id) return

    setIsRendering(true)
    try {
      const result = await renderContract(id)
      if (result.success && result.data) {
        setRenderedHtml(result.data.renderedHtml)
      }
    } catch (err) {
      console.error('Error rendering:', err)
    } finally {
      setIsRendering(false)
    }
  }

  const handleApprove = async () => {
    if (!id) return

    setIsApproving(true)
    try {
      const result = await approveContract(id)
      if (result.success) {
        setApproveModalOpen(false)
        navigate(`/contracts/${id}`)
      } else {
        setError(result.error || 'Error al aprobar el contrato')
      }
    } catch (err) {
      console.error('Error approving:', err)
      setError('Error al aprobar el contrato')
    } finally {
      setIsApproving(false)
    }
  }

  const handleExportDocx = useCallback(async () => {
    if (!id) return

    setIsExportingDocx(true)
    try {
      const result = await exportContractHtml(id)
      if (result.success && result.data) {
        const wordHtml = buildWordHtml(result.data.html)
        const blob = new Blob([wordHtml], {
          type: 'application/msword',
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `contrato-${contract?.orderNumber || 'nuevo'}.doc`
        a.click()
        URL.revokeObjectURL(url)
      } else {
        setError(result.error || 'Error al exportar el contrato')
      }
    } catch (err) {
      console.error('Error exporting DOCX:', err)
      setError('Error al exportar el contrato como DOCX')
    } finally {
      setIsExportingDocx(false)
    }
  }, [id, contract])

  const handlePrint = useCallback(async () => {
    if (!id) return

    setIsPrinting(true)
    try {
      const result = await exportContractHtml(id)
      if (result.success && result.data?.html) {
        printContract(result.data.html)
      } else {
        setError(result.error || 'Error al imprimir el contrato')
      }
    } catch (err) {
      console.error('Error printing:', err)
      setError('Error al imprimir el contrato')
    } finally {
      setIsPrinting(false)
    }
  }, [id])

  const handleExportPdf = useCallback(async () => {
    if (!id) return

    setIsExportingPdf(true)
    try {
      const result = await exportContractPdfBlob(id)
      if (result.success && result.data) {
        const url = URL.createObjectURL(result.data)
        const a = document.createElement('a')
        a.href = url
        a.download = `contrato-${contract?.orderNumber || 'nuevo'}.pdf`
        a.click()
        URL.revokeObjectURL(url)
      } else {
        setError(result.error || 'Error al exportar el contrato')
      }
    } catch (err) {
      console.error('Error exporting PDF:', err)
      setError('Error al exportar el contrato')
    } finally {
      setIsExportingPdf(false)
    }
  }, [id, contract])

  const previewSrcDoc = useMemo(
    () => (renderedHtml ? buildContractSrcDoc(renderedHtml) : null),
    [renderedHtml]
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    )
  }

  const isReadOnly =
    isEditMode && contract?.status !== 'draft' && contract?.status !== 'pending_review'

  return (
    <div>
      <PageHeader
        title={isEditMode ? 'Editar Contrato' : 'Nuevo Contrato'}
        description={
          isEditMode
            ? `Editando contrato para orden ${contract?.orderNumber || ''}`
            : 'Genera un contrato seleccionando una orden y una plantilla'
        }
        breadcrumbs={[
          { label: 'Inicio', href: '/' },
          { label: 'Contratos', href: '/contracts' },
          { label: isEditMode ? 'Editar' : 'Nuevo' },
        ]}
      />

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Editor */}
        <div className="space-y-6">
          {/* Selection Card */}
          <Card>
            <CardHeader title="Seleccion" />
            <div className="p-4 space-y-4">
              {/* Intake Selection */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Orden de Contrato
                </label>
                {isEditMode ? (
                  <div className="text-sm">
                    <span className="font-medium">{contract?.orderNumber}</span>
                    {selectedIntake && (
                      <span className="text-neutral-500 ml-2">
                        - {selectedIntake.property.address}
                      </span>
                    )}
                  </div>
                ) : (
                  <Select
                    options={[
                      { value: '', label: 'Seleccionar orden...' },
                      ...intakes.map((i) => ({
                        value: i.id,
                        label: `${i.orderNumber} - ${i.property.address}`,
                      })),
                    ]}
                    value={intakeId}
                    onChange={(e) => setIntakeId(e.target.value)}
                    disabled={isReadOnly}
                  />
                )}
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Plantilla</label>
                {isEditMode ? (
                  <div className="text-sm">
                    <span className="font-medium">{contract?.templateName}</span>
                  </div>
                ) : (
                  <Select
                    options={[
                      { value: '', label: 'Seleccionar plantilla...' },
                      ...templates
                        .filter((t) => !selectedIntake || t.type === selectedIntake.contractType)
                        .map((t) => ({
                          value: t.id,
                          label: `${t.name} (${t.region}) v${t.version}`,
                        })),
                    ]}
                    value={templateId}
                    onChange={(e) => setTemplateId(e.target.value)}
                    disabled={isReadOnly || !intakeId}
                  />
                )}
              </div>

              {/* Intake Summary */}
              {selectedIntake && (
                <div className="bg-neutral-50 rounded-lg p-3 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-neutral-500">Tipo:</span>{' '}
                      <span className="font-medium">
                        {selectedIntake.contractType === 'residential'
                          ? 'Residencial'
                          : 'Comercial'}
                      </span>
                    </div>
                    <div>
                      <span className="text-neutral-500">Canon:</span>{' '}
                      <span className="font-medium">
                        {formatCurrency(selectedIntake.terms.monthlyRent)}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-neutral-500">Inmueble:</span>{' '}
                      <span className="font-medium">{selectedIntake.property.address}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-neutral-500">Periodo:</span>{' '}
                      <span className="font-medium">
                        {formatDate(selectedIntake.terms.startDate)} -{' '}
                        {formatDate(selectedIntake.terms.endDate)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Clauses Card */}
          <Card>
            <CardHeader title="Clausulas Adicionales" />
            <div className="p-4 max-h-[400px] overflow-y-auto">
              {Object.entries(groupedClauses).length === 0 ? (
                <p className="text-sm text-neutral-500">
                  No hay clausulas disponibles para este tipo de contrato.
                </p>
              ) : (
                Object.entries(groupedClauses).map(([category, categoryClauses]) => (
                  <div key={category} className="mb-4">
                    <h4 className="text-sm font-medium text-neutral-600 mb-2">
                      {categoryLabels[category] || category}
                    </h4>
                    <div className="space-y-2">
                      {categoryClauses.map((clause) => (
                        <label
                          key={clause.id}
                          className="flex items-start gap-2 p-2 rounded hover:bg-neutral-50 cursor-pointer"
                        >
                          <Checkbox
                            checked={selectedClauseIds.includes(clause.id)}
                            onChange={() => handleClauseToggle(clause.id)}
                            disabled={isReadOnly}
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium">{clause.title}</div>
                            <div className="text-xs text-neutral-500">{clause.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Field Overrides Card */}
          {selectedTemplate && fieldOverridesList.length > 0 && (
            <Card>
              <div
                className="cursor-pointer flex items-start justify-between"
                onClick={() => setShowOverrides(!showOverrides)}
              >
                <CardHeader
                  title="Sobreescribir Campos"
                  action={
                    showOverrides ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )
                  }
                />
              </div>
              {showOverrides && (
                <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
                  {fieldOverridesList.map((field) => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        {field.label}
                        {field.originalValue && (
                          <span className="font-normal text-neutral-400 ml-1">
                            (original: {field.originalValue})
                          </span>
                        )}
                      </label>
                      <Input
                        value={field.value}
                        onChange={(e) => handleFieldOverrideChange(field.key, e.target.value)}
                        placeholder={field.originalValue || 'Dejar vacio para usar valor original'}
                        disabled={isReadOnly}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Actions */}
          <Card padding="sm">
            <div className="flex flex-wrap gap-2">
              {!isReadOnly && (
                <Button onClick={handleSave} loading={isSaving}>
                  <Save className="w-4 h-4 mr-1" />
                  {isEditMode ? 'Guardar Cambios' : 'Crear Contrato'}
                </Button>
              )}
              {isEditMode && !isReadOnly && (
                <>
                  <Button variant="outline" onClick={handleRender} loading={isRendering}>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Actualizar Vista
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setApproveModalOpen(true)}
                    className="text-success-600 border-success-600 hover:bg-success-50"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Aprobar
                  </Button>
                </>
              )}
              {isEditMode && renderedHtml && (
                <>
                  <Button variant="outline" onClick={handleExportPdf} loading={isExportingPdf}>
                    <Download className="w-4 h-4 mr-1" />
                    Exportar PDF
                  </Button>
                  <Button variant="outline" onClick={handlePrint} loading={isPrinting}>
                    <Printer className="w-4 h-4 mr-1" />
                    Imprimir
                  </Button>
                  <Button variant="outline" onClick={handleExportDocx} loading={isExportingDocx}>
                    <FileText className="w-4 h-4 mr-1" />
                    Exportar Word
                  </Button>
                </>
              )}
            </div>
          </Card>
        </div>

        {/* Right Panel - Preview */}
        <div>
          <Card className="sticky top-4">
            <CardHeader
              title="Vista Previa"
              action={
                <div className="flex items-center gap-2">
                  {contract && (
                    <Badge
                      variant={
                        contract.status === 'draft'
                          ? 'default'
                          : contract.status === 'pending_review'
                            ? 'warning'
                            : contract.status === 'approved'
                              ? 'success'
                              : 'primary'
                      }
                    >
                      {contract.status === 'draft'
                        ? 'Borrador'
                        : contract.status === 'pending_review'
                          ? 'En revision'
                          : contract.status === 'approved'
                            ? 'Aprobado'
                            : contract.status === 'sent'
                              ? 'Enviado'
                              : 'Firmado'}
                    </Badge>
                  )}
                  {renderedHtml && (
                    <button
                      onClick={() => setFullscreenOpen(true)}
                      className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-neutral-700"
                      title="Ver en pantalla completa"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              }
            />
            <div className="p-4">
              {previewSrcDoc ? (
                <div className="border border-neutral-200 rounded-lg bg-white overflow-hidden">
                  <iframe
                    srcDoc={previewSrcDoc}
                    className="w-full border-0"
                    style={{ height: '70vh' }}
                    title="Vista previa del contrato"
                  />
                </div>
              ) : intakeId && templateId ? (
                <EmptyState
                  icon={<Eye className="w-8 h-8" />}
                  title="Sin vista previa"
                  description="Guarda el contrato para ver la vista previa renderizada"
                />
              ) : (
                <EmptyState
                  icon={<FileCheck className="w-8 h-8" />}
                  title="Selecciona orden y plantilla"
                  description="Selecciona una orden de contrato y una plantilla para comenzar"
                />
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Approve Modal */}
      <Modal
        open={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        title="Aprobar Contrato"
        description="El contrato quedara listo para enviar"
      >
        <p className="text-neutral-600">
          Estas seguro de que deseas aprobar este contrato? Una vez aprobado no podra ser editado.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setApproveModalOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleApprove} loading={isApproving}>
            Aprobar Contrato
          </Button>
        </ModalFooter>
      </Modal>

      {/* Fullscreen Preview Modal */}
      <Modal open={fullscreenOpen} onClose={() => setFullscreenOpen(false)} size="fullscreen">
        <div className="flex items-center justify-between px-6 py-3 border-b border-neutral-200 -mx-6 -mt-6 mb-0">
          <h2 className="text-lg font-semibold text-neutral-900">Vista Previa</h2>
          <button
            onClick={() => setFullscreenOpen(false)}
            className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-neutral-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {previewSrcDoc && (
          <iframe
            srcDoc={previewSrcDoc}
            className="w-full border-0 -mx-6 -mb-6 flex-1"
            style={{ marginTop: 0, minHeight: '80vh' }}
            title="Vista previa del contrato completa"
          />
        )}
      </Modal>
    </div>
  )
}
